import { Link } from 'react-router-dom'
import { PublicNavBar } from '../components/PublicNavBar'
import { PublicFooter } from '../components/PublicFooter'
import { usePageMeta } from '../hooks/usePageMeta'
import { pageMeta } from '../seo/pageMeta'
import posts from '../content/blog/posts.json'

export function formatPostDate(iso: string): string {
  return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

type Post = (typeof posts)[number]

function PostCard({ post, featured = false }: { post: Post; featured?: boolean }) {
  return (
    <article
      className={`group relative rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 transition-all hover:border-primary-300 dark:hover:border-primary-800 hover:shadow-lg hover:shadow-primary-500/5 ${
        featured ? 'p-8 sm:p-10' : 'p-6'
      }`}
    >
      {featured && (
        <div className="h-1 w-16 rounded-full bg-gradient-to-r from-primary-600 to-orange-500 mb-6" />
      )}
      <p className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 mb-3">
        <time dateTime={post.date}>{formatPostDate(post.date)}</time>
        {featured && (
          <span className="px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-xs font-medium">
            Latest
          </span>
        )}
      </p>
      <h2
        className={
          featured
            ? 'text-2xl sm:text-3xl font-bold tracking-tight [text-wrap:balance]'
            : 'text-xl font-semibold tracking-tight [text-wrap:balance]'
        }
      >
        <Link
          to={`/blog/${post.slug}`}
          className="after:absolute after:inset-0 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors"
        >
          {post.title}
        </Link>
      </h2>
      <p className="mt-3 text-gray-600 dark:text-gray-300 leading-relaxed text-[15px]">
        {post.description}
      </p>
      <p className="mt-4 flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400">
        Read post
        <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
          &rarr;
        </span>
      </p>
    </article>
  )
}

export default function Blog() {
  usePageMeta(pageMeta('/blog'))

  const sorted = [...posts].sort((a, b) => b.date.localeCompare(a.date))
  const [latest, ...rest] = sorted

  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-100">
      <PublicNavBar />

      <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
        <header className="mb-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary-600 dark:text-primary-400 mb-3">
            Blog
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight mb-4">
            <span className="bg-gradient-to-r from-gray-900 via-gray-700 to-gray-500 dark:from-white dark:via-gray-200 dark:to-gray-400 bg-clip-text text-transparent">
              Notes from the factory floor
            </span>
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Engineering notes from the team building Factory Nexus.
          </p>
        </header>

        {latest && <PostCard post={latest} featured />}
        {rest.length > 0 && (
          <div className="mt-6 grid sm:grid-cols-2 gap-6">
            {rest.map((post) => (
              <PostCard key={post.slug} post={post} />
            ))}
          </div>
        )}
      </div>

      <PublicFooter />
    </div>
  )
}
